import React, { useState } from 'react';
import MicrositeLayout from '../MicrositeLayout.jsx';
import {
  getSocialUrl,
  extractYouTubeId,
  isYouTubeShort,
  linkIconMap,
  tipoEventoLabels,
  getFontById,
  isMicrositeSectionHidden,
  MICROSITE_SECTION_EMPTY_HINT,
  getMicrositeSectionLabel,
  getMicrositeHeroImageUrl,
  getCabeceraHeroVisibilidad,
  normalizeContactoMostrar,
  isMicrositeContactoSectionVisuallyEmpty,
  getMicrositeBodySectionOrder,
  micrositeCalendarioActuacionesVisibles,
  micrositeCalendarioEnsayosVisibles } from './micrositeUtils';
import { ScrollReveal,
  ScrollRevealSoft } from './MicrositeScrollReveal.jsx';
import MicrositePlantillaMusicos from './MicrositePlantillaMusicos.jsx';
import MicrositeLogrosBlock from './MicrositeLogrosBlock.jsx';
import MicrositeAvalesBlock from './MicrositeAvalesBlock.jsx';
import MicrositeResenasBlock from './MicrositeResenasBlock.jsx';
import MicrositeNoticiasBlock from './MicrositeNoticiasBlock.jsx';
import MicrositeCalendario from './MicrositeCalendario.jsx';
import {
  Box,
  Container,
  VStack,
  HStack,
  Heading,
  Text,
  Badge,
  Image,
  Icon,
  SimpleGrid,
  Flex,
  Wrap,
  WrapItem,
  Link,
  Button,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Avatar } from '@/components/ui/avatar';
import { Divider } from '@/components/ui/chakra-v2-compat';
import { Tooltip } from '@/components/ui/tooltip';
import {
  FaMapMarkerAlt, FaUsers, FaCalendarAlt, FaCheckCircle, FaStar, FaRegStar,
  FaMusic, FaYoutube, FaClock, FaChurch, FaInstagram, FaFacebook,
  FaEnvelope, FaPhone, FaExternalLinkAlt, FaQuoteLeft,
  FaGlobe, FaLink,
} from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';

const SectionCard = ({ id, children, py, sx }) => (
  <Box as="section" id={id} scrollMarginTop="70px" py={py || { base: 8, md: 10 }} sx={sx}>
    <Container maxW="5xl">
      <Box bg="white" border="1px solid" borderColor="gray.200" rounded="lg" shadow="sm" p={{ base: 6, md: 10 }}>
        {children}
      </Box>
    </Container>
  </Box>
);

const SectionTitle = ({ children, fontFamily }) => (
  <VStack gap={2} mb={{ base: 6, md: 8 }}>
    <Heading size={{ base: 'md', md: 'lg' }} fontFamily={fontFamily || "'Georgia', 'Times New Roman', serif"} color="gray.800" fontWeight="700" textAlign="center">
      {children}
    </Heading>
    <Box w="40px" h="2px" bg="gray.400" />
  </VStack>
);

const MicrositeClasico = ({
  bandaData, avales, stats, sections, msConfig, contacto, social, ensayosPublicos, eventosPublicos, actuacionesPublicas, locationText, hasSocials,
  primaryColor, surfaceColor = '#FFFFFF',
}) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAllRepertoire, setShowAllRepertoire] = useState(false);
  const repertorioVisible = showAllRepertoire ? bandaData.repertorio : (bandaData.repertorio || []).slice(0, 15);
  const headingFont = getFontById(msConfig.tipografia).family || "'Georgia', 'Times New Roman', serif";
  const secHidden = (id) => isMicrositeSectionHidden(id, msConfig);
  const enlacesItems = msConfig.enlacesPersonalizados || [];
  const heroImageUrl = getMicrositeHeroImageUrl(bandaData);
  const hv = getCabeceraHeroVisibilidad(msConfig);
  const heroHasSocials = hv.mostrarRrss && hasSocials;
  const musicosPlantilla = Array.isArray(bandaData.musicos) ? bandaData.musicos : [];
  const showPlantillaMusicos = msConfig.mostrarPlantillaMusicosNosotros === true && musicosPlantilla.length > 0;
  const showEspecialidadesWeb = msConfig.mostrarEspecialidadesNosotros !== false;
  const hasNosotrosContent =
    Boolean(bandaData.bio) ||
    (showEspecialidadesWeb && bandaData.especialidades?.length > 0) ||
    showPlantillaMusicos;
  const cm = normalizeContactoMostrar(msConfig.contactoMostrar);
  const contactoBlockEmpty = isMicrositeContactoSectionVisuallyEmpty(
    contacto,
    locationText,
    hasSocials,
    msConfig.textoContratar,
    msConfig,
  );

  const bodySectionOrder = getMicrositeBodySectionOrder(msConfig);
  const secSx = (id) => ({ order: bodySectionOrder.indexOf(id) });
  const calActsVis = micrositeCalendarioActuacionesVisibles(actuacionesPublicas, msConfig);
  const calEnsVis = micrositeCalendarioEnsayosVisibles(ensayosPublicos, msConfig);

  return (
    <MicrositeLayout bandaData={bandaData} micrositeConfig={msConfig} sections={sections} tema="clasico">

      {/* HEADER COMPACTO */}
      <Box id="inicio" position="relative" h={{ base: '50vh', md: '45vh' }} minH="320px" overflow="hidden" scrollMarginTop="60px">
        <Box position="absolute" inset={0}
          backgroundImage={heroImageUrl ? `url(${heroImageUrl})` : undefined}
          backgroundSize="cover" backgroundPosition="center"
          filter="blur(2px) brightness(0.4)" transform="scale(1.05)"
        />
        <Flex position="relative" zIndex={1} h="full" align="center" justify="center">
          <VStack gap={4} textAlign="center" px={4}>
            <ScrollReveal>
              <Avatar.Root size="2xl" border="4px solid white" shadow="xl" w="120px" h="120px"><Avatar.Fallback name={bandaData.nombre} /><Avatar.Image src={bandaData.avatar} /></Avatar.Root>
            </ScrollReveal>
            <ScrollRevealSoft delay={0.06}>
              <VStack gap={1}>
                <HStack gap={2} justify="center">
                  <Heading size={{ base: 'xl', md: '2xl' }} color="white" fontFamily={headingFont} fontWeight="700">
                    {bandaData.nombre}
                  </Heading>
                  {hv.mostrarVerificado && bandaData.verificationStatus === 'VERIFIED' && <Tooltip content="Perfil verificado" showArrow><span><Icon color="blue.300" w={5} h={5} asChild><FaCheckCircle /></Icon></span></Tooltip>}
                </HStack>
                {hv.mostrarUbicacion && locationText && <HStack gap={2} color="whiteAlpha.900"><Icon boxSize={3.5} asChild><FaMapMarkerAlt /></Icon><Text fontSize="md" fontWeight="500">{locationText}</Text></HStack>}
              </VStack>
            </ScrollRevealSoft>
            <ScrollRevealSoft delay={0.12}>
              <Wrap gap={3} justify="center">
                {hv.mostrarNMusicos && bandaData.nMusicos && <WrapItem><Badge bg="whiteAlpha.200" color="white" px={3} py={1} fontSize="xs" fontWeight="600" rounded="md"><Icon mr={1.5} asChild><FaUsers /></Icon>{bandaData.nMusicos} musicos</Badge></WrapItem>}
                {hv.mostrarAnoFundacion && bandaData.anoFundacion && <WrapItem><Badge bg="whiteAlpha.200" color="white" px={3} py={1} fontSize="xs" fontWeight="600" rounded="md"><Icon mr={1.5} asChild><FaCalendarAlt /></Icon>Desde {bandaData.anoFundacion}</Badge></WrapItem>}
                {hv.mostrarTipoBanda && bandaData.tipoGrupo && <WrapItem><Badge bg="whiteAlpha.200" color="white" px={3} py={1} fontSize="xs" fontWeight="600" rounded="md"><Icon mr={1.5} asChild><FaMusic /></Icon>{bandaData.tipoGrupo}</Badge></WrapItem>}
              </Wrap>
            </ScrollRevealSoft>
            {heroHasSocials && (
              <ScrollRevealSoft delay={0.18}>
                <HStack gap={3} mt={1}>
                  {social.instagram && <Link
                    href={getSocialUrl('instagram', social.instagram)}
                    target='_blank'
                    rel='noopener noreferrer'><Icon
                    boxSize={5}
                    color="whiteAlpha.800"
                    _hover={{ color: 'white' }}
                    transition="color 0.2s"
                    asChild><FaInstagram /></Icon></Link>}
                  {social.facebook && <Link
                    href={getSocialUrl('facebook', social.facebook)}
                    target='_blank'
                    rel='noopener noreferrer'><Icon
                    boxSize={5}
                    color="whiteAlpha.800"
                    _hover={{ color: 'white' }}
                    transition="color 0.2s"
                    asChild><FaFacebook /></Icon></Link>}
                  {social.youtube && <Link
                    href={getSocialUrl('youtube', social.youtube)}
                    target='_blank'
                    rel='noopener noreferrer'><Icon
                    boxSize={5}
                    color="whiteAlpha.800"
                    _hover={{ color: 'white' }}
                    transition="color 0.2s"
                    asChild><FaYoutube /></Icon></Link>}
{social.twitter && <Link
                    href={getSocialUrl('twitter', social.twitter)}
                    target='_blank'
                    rel='noopener noreferrer'><Icon
                    boxSize={5}
                    color="whiteAlpha.800"
                    _hover={{ color: 'white' }}
                    transition="color 0.2s"
                    asChild><FaXTwitter /></Icon></Link>}
                </HStack>
              </ScrollRevealSoft>
            )}
          </VStack>
        </Flex>
      </Box>

      <Flex direction="column" bg={surfaceColor} pb={12} w="full">

        {/* SOBRE NOSOTROS */}
        {!secHidden('nosotros') && (
          <SectionCard id="nosotros" sx={secSx('nosotros')}>
            <ScrollReveal><SectionTitle fontFamily={headingFont}>{getMicrositeSectionLabel('nosotros')}</SectionTitle></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {hasNosotrosContent ? (
                <>
                  {bandaData.bio && (
                    <Text fontSize="md" lineHeight="1.9" color="gray.600" textAlign="center" whiteSpace="pre-line" maxW="3xl" mx="auto">{bandaData.bio}</Text>
                  )}
                  {showEspecialidadesWeb && bandaData.especialidades?.length > 0 && (
                    <Wrap gap={2} justify="center" mt={bandaData.bio ? 6 : 0}>
                      {bandaData.especialidades.map((esp, i) => <WrapItem key={i}><Badge colorPalette="gray" variant="outline" px={3} py={1} rounded="md" fontSize="xs" fontWeight="600" borderColor="gray.300">{esp}</Badge></WrapItem>)}
                    </Wrap>
                  )}
                  {showPlantillaMusicos && (
                    <MicrositePlantillaMusicos musicos={musicosPlantilla} micrositeConfig={msConfig} primaryColor={primaryColor} variant="clasico" />
                  )}
                </>
              ) : (
                <Text fontSize="md" lineHeight="1.8" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.nosotros}</Text>
              )}
            </ScrollRevealSoft>
          </SectionCard>
        )}

        {/* HISTORIA */}
        {!secHidden('historia') && (
          <SectionCard id="historia" sx={secSx('historia')}>
            <ScrollReveal><SectionTitle fontFamily={headingFont}>{getMicrositeSectionLabel('historia')}</SectionTitle></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {msConfig.historia ? (
                <Text fontSize="md" lineHeight="1.9" color="gray.600" textAlign="center" whiteSpace="pre-line">{msConfig.historia}</Text>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.historia}</Text>
              )}
            </ScrollRevealSoft>
          </SectionCard>
        )}

        {/* NOTICIAS */}
        {!secHidden('noticias') && (
          <SectionCard id="noticias" sx={secSx('noticias')}>
            <ScrollReveal><SectionTitle fontFamily={headingFont}>{getMicrositeSectionLabel('noticias')}</SectionTitle></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              <MicrositeNoticiasBlock msConfig={msConfig} primaryColor={primaryColor} />
            </ScrollRevealSoft>
          </SectionCard>
        )}

        {/* DIRECTIVA */}
        {!secHidden('directiva') && (
          <SectionCard id="directiva" sx={secSx('directiva')}>
            <ScrollReveal><SectionTitle fontFamily={headingFont}>{getMicrositeSectionLabel('directiva')}</SectionTitle></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {(msConfig.directiva?.length > 0) ? (
                <Flex flexWrap="wrap" justify="center" align="flex-start" gap={{ base: 6, md: 8 }} w="full">
                  {msConfig.directiva.map((m, i) => (
                    <VStack key={m.id || i} gap={3} textAlign="center" align="center" minW="140px" maxW="220px" flex="0 0 auto" p={4} bg={surfaceColor} rounded="lg" border="1px solid" borderColor="gray.100">
                      <Avatar.Root size="md" bg="gray.200" color="gray.600"><Avatar.Fallback name={m.nombre} /><Avatar.Image src={m.foto || undefined} /></Avatar.Root>
                      <VStack gap={0}><Text fontWeight="700" fontSize="sm" color="gray.800">{m.nombre}</Text><Text fontSize="xs" color="gray.500">{m.cargo}</Text></VStack>
                    </VStack>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.directiva}</Text>
              )}
            </ScrollRevealSoft>
          </SectionCard>
        )}

        {/* GALERIA */}
        {!secHidden('galeria') && (
          <SectionCard id="galeria" sx={secSx('galeria')}>
            <ScrollReveal><SectionTitle fontFamily={headingFont}>Galeria</SectionTitle></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {bandaData.fotos?.length > 0 ? (
                <Flex flexWrap="wrap" justifyContent="center" alignItems="stretch" gap={4} w="full">
                  {bandaData.fotos.map((foto, i) => (
                    <Box key={i} flex={{ base: '1 1 100%', md: '0 1 440px' }} maxW={{ base: '100%', md: 'min(100%, 460px)' }} minW={0} overflow="hidden" rounded="lg" cursor="pointer" onClick={() => setSelectedImage(foto)} role="group" border="1px solid" borderColor="gray.100">
                      <Image src={foto} alt={`Foto ${i + 1}`} w="full" h={{ base: '200px', md: '260px' }} objectFit="cover" transition="transform 0.3s ease" _groupHover={{ transform: 'scale(1.03)' }} />
                    </Box>
                  ))}
                </Flex>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.galeria}</Text>
              )}
            </ScrollRevealSoft>
          </SectionCard>
        )}

        {/* VIDEOS */}
        {!secHidden('videos') && (
          <SectionCard id="videos" sx={secSx('videos')}>
            <ScrollReveal><SectionTitle fontFamily={headingFont}>{getMicrositeSectionLabel('videos')}</SectionTitle></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {bandaData.videos?.length > 0 ? (() => {
                const normalVideos = bandaData.videos.filter((v) => !isYouTubeShort(v.url));
                const shorts = bandaData.videos.filter((v) => isYouTubeShort(v.url));
                const iframeAllow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
                return (
                  <VStack gap={10} align="center" w="full">
                    {normalVideos.length > 0 && (
                      <Box w="full">
                        <Text fontSize="xs" fontWeight="700" color="gray.500" mb={3} textTransform="uppercase" letterSpacing="0.06em" textAlign="center" w="full">
                          Vídeos
                        </Text>
                        <Flex flexWrap="wrap" justifyContent="center" alignItems="stretch" gap={6} w="full" maxW="960px" mx="auto">
                          {normalVideos.map((video, i) => {
                            const videoId = extractYouTubeId(video.url);
                            return (
                              <Box key={video.id ?? `v-n-${i}`} flex={{ base: '1 1 100%', md: '0 1 440px' }} maxW={{ base: '100%', md: 'min(100%, 460px)' }} minW={0} rounded="lg" overflow="hidden" border="1px solid" borderColor="gray.200">
                                {videoId ? (
                                  <Box position="relative" pb="56.25%">
                                    <iframe src={`https://www.youtube.com/embed/${videoId}`} title={video.titulo} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} frameBorder="0" allow={iframeAllow} allowFullScreen />
                                  </Box>
                                ) : (
                                  <Box h="180px" bg="gray.100" display="flex" alignItems="center" justifyContent="center">
                                    <Icon w={12} h={12} color="gray.300" asChild><FaYoutube /></Icon>
                                  </Box>
                                )}
                                {video.titulo && (
                                  <Box p={3} bg={surfaceColor}>
                                    <Text fontSize="sm" fontWeight="600" color="gray.700" textAlign="center">
                                      {video.titulo}
                                    </Text>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Flex>
                      </Box>
                    )}
                    {shorts.length > 0 && (
                      <Box w="full">
                        <Text fontSize="xs" fontWeight="700" color="gray.500" mb={3} textTransform="uppercase" letterSpacing="0.06em" textAlign="center" w="full">
                          Shorts
                        </Text>
                        <Flex flexWrap="wrap" justifyContent="center" alignItems="flex-start" gap={{ base: 3, md: 4 }} rowGap={5} w="full">
                          {shorts.map((video, i) => {
                            const videoId = extractYouTubeId(video.url);
                            return (
                              <Box key={video.id ?? `v-s-${i}`} flex="0 0 auto" w={{ base: 'calc(50% - 6px)', sm: '200px', md: '240px' }} maxW="260px" display="flex" flexDirection="column" alignItems="center">
                                <Box rounded="lg" overflow="hidden" border="1px solid" borderColor="gray.200" w="full" maxW="260px">
                                  {videoId ? (
                                    <Box position="relative" pb="177.78%" w="full">
                                      <iframe src={`https://www.youtube.com/embed/${videoId}`} title={video.titulo} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} frameBorder="0" allow={iframeAllow} allowFullScreen />
                                    </Box>
                                  ) : (
                                    <Box h="120px" bg="gray.100" display="flex" alignItems="center" justifyContent="center">
                                      <Icon w={8} h={8} color="gray.300" asChild><FaYoutube /></Icon>
                                    </Box>
                                  )}
                                  {video.titulo && (
                                    <Box p={2} bg={surfaceColor}>
                                      <Text fontSize="xs" fontWeight="600" color="gray.700" lineClamp={2} textAlign="center">
                                        {video.titulo}
                                      </Text>
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Flex>
                      </Box>
                    )}
                  </VStack>
                );
              })() : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.videos}</Text>
              )}
            </ScrollRevealSoft>
          </SectionCard>
        )}

        {/* REPERTORIO */}
        {!secHidden('repertorio') && (
          <SectionCard id="repertorio" sx={secSx('repertorio')}>
            <ScrollReveal><SectionTitle fontFamily={headingFont}>{getMicrositeSectionLabel('repertorio')}</SectionTitle></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {bandaData.repertorio?.length > 0 ? (
                <>
                  <Text textAlign="center" color="gray.500" fontSize="sm" mb={4}>{bandaData.repertorio.length} obras en nuestro repertorio</Text>
                  <Box rounded="lg" overflow="hidden" border="1px solid" borderColor="gray.200">
                    <Flex bg={primaryColor} px={5} py={3}><Text color="white" fontWeight="700" fontSize="sm" flex={1}>Obra</Text><Text color="white" fontWeight="700" fontSize="sm" w="120px" display={{ base: 'none', md: 'block' }}>Autor</Text><Text color="white" fontWeight="700" fontSize="sm" w="100px" display={{ base: 'none', md: 'block' }}>Tipo</Text></Flex>
                    {repertorioVisible.map((item, i) => {
                      const nombre = item.nombre || item.marcha?.nombre || '';
                      const autor = item.autor || item.marcha?.autor || '';
                      const tipo = item.tipo || item.marcha?.tipo || '';
                      return (
                        <Flex key={item.id || i} px={5} py={3} align="center" bg={i % 2 === 0 ? 'white' : 'gray.50'} borderBottom="1px solid" borderColor="gray.100">
                          <VStack align="start" gap={0} flex={1}>
                            <Text fontWeight="600" fontSize="sm" color="gray.800">{nombre}</Text>
                            <HStack gap={2} display={{ base: 'flex', md: 'none' }}>{autor && <Text fontSize="xs" color="gray.500">{autor}</Text>}{tipo && <Badge colorPalette="gray" variant="subtle" fontSize="10px">{tipo}</Badge>}</HStack>
                          </VStack>
                          <Text fontSize="sm" color="gray.600" w="120px" display={{ base: 'none', md: 'block' }}>{autor}</Text>
                          <Text fontSize="sm" color="gray.500" w="100px" display={{ base: 'none', md: 'block' }}>{tipo}</Text>
                        </Flex>
                      );
                    })}
                  </Box>
                  {bandaData.repertorio.length > 15 && !showAllRepertoire && (
                    <Flex justify="center" mt={4}><Button variant="outline" size="sm" rounded="md" onClick={() => setShowAllRepertoire(true)} borderColor="gray.300" color="gray.600">Ver todo ({bandaData.repertorio.length})</Button></Flex>
                  )}
                </>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.repertorio}</Text>
              )}
            </ScrollRevealSoft>
          </SectionCard>
        )}

        {/* LOGROS */}
        {!secHidden('logros') && (
          <SectionCard id="logros" sx={secSx('logros')}>
            <ScrollReveal><SectionTitle fontFamily={headingFont}>{getMicrositeSectionLabel('logros')}</SectionTitle></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {(msConfig.logros?.length > 0) ? (
                <MicrositeLogrosBlock logros={msConfig.logros} primaryColor={primaryColor} surfaceColor={surfaceColor} variant="clasico" />
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.logros}</Text>
              )}
            </ScrollRevealSoft>
          </SectionCard>
        )}

        {/* AVALES Y RESENAS */}
        {!secHidden('resenas') && (
          <SectionCard id="resenas" sx={secSx('resenas')}>
            <ScrollReveal><SectionTitle fontFamily={headingFont}>{getMicrositeSectionLabel('resenas')}</SectionTitle></ScrollReveal>
            {(avales.length > 0 || (bandaData.resenasRecibidas?.filter(r => r.isPublished).length > 0)) ? (
            <>
            {avales.length > 0 && (
              <ScrollRevealSoft delay={0.08}>
                <MicrositeAvalesBlock
                  avales={avales}
                  variant="clasico"
                  surfaceColor={surfaceColor}
                  mb={bandaData.resenasRecibidas?.filter(r => r.isPublished).length > 0 ? 8 : 0}
                />
              </ScrollRevealSoft>
            )}
            {bandaData.resenasRecibidas?.filter(r => r.isPublished).length > 0 && (
              <ScrollRevealSoft delay={avales.length > 0 ? 0.15 : 0.08}>
                <MicrositeResenasBlock
                  resenas={bandaData.resenasRecibidas.filter((r) => r.isPublished)}
                  stats={stats}
                  variant="clasico"
                  primaryColor={primaryColor}
                  surfaceColor={surfaceColor}
                />
              </ScrollRevealSoft>
            )}
            </>
            ) : (
              <ScrollRevealSoft delay={0.08}>
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.resenas}</Text>
              </ScrollRevealSoft>
            )}
          </SectionCard>
        )}

        {/* CALENDARIO */}
        {!secHidden('calendario') && (
          <SectionCard id="calendario" sx={secSx('calendario')}>
            <ScrollReveal><SectionTitle fontFamily={headingFont}>{getMicrositeSectionLabel('calendario')}</SectionTitle></ScrollReveal>
            {(((msConfig.calendarioMostrarActuaciones !== false) && calActsVis.length > 0) || ((msConfig.calendarioMostrarEnsayos !== false) && calEnsVis.length > 0)) ? (
              <ScrollRevealSoft delay={0.08}>
                <MicrositeCalendario
                  msConfig={msConfig}
                  actuaciones={(msConfig.calendarioMostrarActuaciones !== false) ? calActsVis : []}
                  ensayos={(msConfig.calendarioMostrarEnsayos !== false) ? calEnsVis : []}
                  light={false}
                />
              </ScrollRevealSoft>
            ) : (
              <ScrollRevealSoft delay={0.08}>
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.calendario}</Text>
              </ScrollRevealSoft>
            )}
          </SectionCard>
        )}

        {/* PATROCINADORES */}
        {!secHidden('patrocinadores') && (
          <SectionCard id="patrocinadores" sx={secSx('patrocinadores')}>
            <ScrollReveal><SectionTitle fontFamily={headingFont}>{getMicrositeSectionLabel('patrocinadores')}</SectionTitle></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {(msConfig.patrocinadores?.length > 0) ? (
                <Flex wrap="wrap" justify="center" gap={8}>
                  {msConfig.patrocinadores.map((p, i) => {
                    const content = p.logo ? <Image src={p.logo} alt={p.nombre} h="50px" w="auto" maxW="140px" objectFit="contain" opacity={0.6} _hover={{ opacity: 1 }} transition="opacity 0.3s" /> : <Text fontSize="sm" fontWeight="600" color="gray.500">{p.nombre}</Text>;
                    return (
                      <VStack key={i} gap={1}>
                        {p.url ? <Link
                          href={p.url.startsWith('http') ? p.url : `https://${p.url}`}
                          target='_blank'
                          rel='noopener noreferrer'>{content}</Link> : content}
                        <Text fontSize="xs" color="gray.400">{p.nombre}</Text>
                      </VStack>
                    );
                  })}
                </Flex>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.patrocinadores}</Text>
              )}
            </ScrollRevealSoft>
          </SectionCard>
        )}

        {!secHidden('enlaces') && (
          <SectionCard id="enlaces" sx={secSx('enlaces')}>
            <ScrollReveal><SectionTitle fontFamily={headingFont}>{getMicrositeSectionLabel('enlaces')}</SectionTitle></ScrollReveal>
            <ScrollRevealSoft delay={0.08}>
              {enlacesItems.length > 0 ? (
                <VStack gap={2} align="center">
                  {enlacesItems.map((e, i) => {
                    const LinkIcon = linkIconMap[e.icono] || FaLink;
                    return (
                      <Link
                        key={i}
                        href={e.url?.startsWith('http') ? e.url : `https://${e.url || ''}`}
                        target='_blank'
                        rel='noopener noreferrer'>
                        <HStack gap={2} color={primaryColor}><Icon boxSize={4} asChild><LinkIcon /></Icon><Text fontSize="sm" fontWeight="600">{e.titulo || e.url}</Text></HStack>
                      </Link>
                    );
                  })}
                </VStack>
              ) : (
                <Text fontSize="md" color="gray.500" textAlign="center">{MICROSITE_SECTION_EMPTY_HINT.enlaces}</Text>
              )}
            </ScrollRevealSoft>
          </SectionCard>
        )}

        {/* CONTACTO */}
        {!secHidden('contacto') && (
        <Box as="section" id="contacto" scrollMarginTop="70px" py={{ base: 8, md: 10 }} sx={secSx('contacto')}>
          <Container maxW="5xl">
            <Box bg="white" border="1px solid" borderColor="gray.200" rounded="lg" shadow="sm" p={{ base: 6, md: 10 }}>
              <ScrollReveal>
                <SectionTitle fontFamily={headingFont}>{getMicrositeSectionLabel('contacto')}</SectionTitle>
              </ScrollReveal>
              {msConfig.textoContratar && cm.textoContratar && (
                <ScrollRevealSoft delay={0.08}>
                  <Text color="gray.600" textAlign="center" fontSize="md" lineHeight="1.8" mb={8} maxW="3xl" mx="auto">{msConfig.textoContratar}</Text>
                </ScrollRevealSoft>
              )}
              <ScrollRevealSoft delay={msConfig.textoContratar && cm.textoContratar ? 0.14 : 0.08}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={8} maxW="2xl" mx="auto">
                  <VStack align="start" gap={4}>
                    {contacto.email && cm.email && <Link href={`mailto:${contacto.email}`} _hover={{ textDecoration: 'none' }}><HStack gap={3} _hover={{ color: primaryColor }} transition="color 0.2s"><Icon color={primaryColor} asChild><FaEnvelope /></Icon><Text fontSize="sm" color="gray.700">{contacto.email}</Text></HStack></Link>}
                    {contacto.telefono && cm.telefono && <Link href={`tel:${contacto.telefono}`} _hover={{ textDecoration: 'none' }}><HStack gap={3}><Icon color={primaryColor} asChild><FaPhone /></Icon><Text fontSize="sm" color="gray.700">{contacto.telefono}</Text></HStack></Link>}
                    {contacto.website && cm.website && <Link
                      href={contacto.website.startsWith('http') ? contacto.website : `https://${contacto.website}`}
                      target='_blank'
                      rel='noopener noreferrer'><HStack gap={3}><Icon color={primaryColor} asChild><FaGlobe /></Icon><Text fontSize="sm" color="gray.700">{contacto.website}</Text></HStack></Link>}
                    {locationText && cm.ubicacion && <HStack gap={3}><Icon color={primaryColor} asChild><FaMapMarkerAlt /></Icon><Text fontSize="sm" color="gray.700">{locationText}</Text></HStack>}
                  </VStack>
                  <VStack align="start" gap={4}>
                    {hasSocials && cm.redes && (
                      <>
                        <Text color="gray.500" fontWeight="600" fontSize="xs" textTransform="uppercase" letterSpacing="wider">Redes Sociales</Text>
                        <HStack gap={3}>
                          {social.instagram && <Link
                            href={getSocialUrl('instagram', social.instagram)}
                            target='_blank'
                            rel='noopener noreferrer'><Flex w="40px" h="40px" rounded="md" bg="gray.100" align="center" justify="center" _hover={{ bg: 'gray.200' }} transition="all 0.2s"><Icon boxSize={4} color="gray.600" asChild><FaInstagram /></Icon></Flex></Link>}
                          {social.facebook && <Link
                            href={getSocialUrl('facebook', social.facebook)}
                            target='_blank'
                            rel='noopener noreferrer'><Flex w="40px" h="40px" rounded="md" bg="gray.100" align="center" justify="center" _hover={{ bg: 'gray.200' }} transition="all 0.2s"><Icon boxSize={4} color="gray.600" asChild><FaFacebook /></Icon></Flex></Link>}
                          {social.youtube && <Link
                            href={getSocialUrl('youtube', social.youtube)}
                            target='_blank'
                            rel='noopener noreferrer'><Flex w="40px" h="40px" rounded="md" bg="gray.100" align="center" justify="center" _hover={{ bg: 'gray.200' }} transition="all 0.2s"><Icon boxSize={4} color="gray.600" asChild><FaYoutube /></Icon></Flex></Link>}
{social.twitter && <Link
                            href={getSocialUrl('twitter', social.twitter)}
                            target='_blank'
                            rel='noopener noreferrer'><Flex w="40px" h="40px" rounded="md" bg="gray.100" align="center" justify="center" _hover={{ bg: 'gray.200' }} transition="all 0.2s"><Icon boxSize={4} color="gray.600" asChild><FaXTwitter /></Icon></Flex></Link>}
                        </HStack>
                      </>
                    )}
                  </VStack>
                </SimpleGrid>
              </ScrollRevealSoft>
              {contactoBlockEmpty && (
                <ScrollRevealSoft delay={0.08}>
                  <Text fontSize="md" color="gray.500" textAlign="center" mt={4}>{MICROSITE_SECTION_EMPTY_HINT.contacto}</Text>
                </ScrollRevealSoft>
              )}
            </Box>
          </Container>
        </Box>
        )}
      </Flex>

      {/* Image lightbox */}
      <Dialog.Root open={!!selectedImage} size='full' placement='center' onOpenChange={e => {
        if (!e.open) {
          setSelectedImage(null);
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.900" />
          <Dialog.Positioner>
            <Dialog.Content bg="transparent" boxShadow="none">
              <Dialog.CloseTrigger color="white" size="lg" bg="blackAlpha.600" _hover={{ bg: 'blackAlpha.700' }} rounded="full" m={4} zIndex={10} />
              <Dialog.Body display="flex" alignItems="center" justifyContent="center" p={4} onClick={() => setSelectedImage(null)}>
                {selectedImage && <Image src={selectedImage} alt="Imagen" maxH="90vh" maxW="90vw" objectFit="contain" onClick={(e) => e.stopPropagation()} />}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </MicrositeLayout>
  );
};

export default MicrositeClasico;
